import React, { useState, useRef, useEffect } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import PaginationControls from "aldoo-ra/Pagination"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import * as ShareAPI from "../FileShareFeature/api"
import StatusBadge from "./StatusBadge"
import { formatBytes, formatTimeRemaining } from "./utils"

const ITEMS_PER_PAGE = 10

export default function MobileFileList({ auth, statusFilter, refreshKey, onInfo }) {
  const texts = TextBundle({ id: "transfer_share_dashboard" })
  const [search, setSearch] = useState("")
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const inputRef = useRef(null)

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
  const startIndex = (page - 1) * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems)

  useEffect(() => {
    ShareAPI.ListFileShares({
      auth,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
      search: search.trim() ? { file_name: search } : undefined,
      statusFilter,
    }).then((result) => {
      if (!result?.data) return
      setData(result.data)
      setTotalItems(result.total || result.totalItems || 0)
    })
  }, [page, search, statusFilter, refreshKey])

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter])

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <input
          type="text"
          placeholder={texts?.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          ref={inputRef}
          className="w-full px-3 py-2 pr-8 text-sm bg-paper border border-bordered rounded-lg text-text placeholder-text_secondary outline-none focus:border-primary transition-colors"
        />
        {search && (
          <button
            onClick={() => { setSearch(""); inputRef.current?.focus() }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="divide-y divide-bordered rounded-xl border border-bordered overflow-hidden">
        {data.length === 0 && (
          <p className="text-sm text-text_secondary text-center py-8">
            {search ? texts?.noFilesFound : texts?.noSharedFiles}
          </p>
        )}
        {data.map((item) => {
          const timeRemaining = formatTimeRemaining(item)
          return (
            <div
              key={item.orderID}
              onClick={() => onInfo(item)}
              className="flex items-center gap-3 px-4 py-3 bg-paper cursor-pointer hover:bg-neutral_1 active:bg-neutral_1 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text truncate">
                  {item.title || item.file_name}
                </p>
                {item.title && (
                  <p className="text-xs text-text_secondary truncate">{item.file_name}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {item.file_size > 0 && (
                    <span className="text-xs text-text_secondary">
                      {formatBytes(item.file_size)}
                    </span>
                  )}
                  {timeRemaining && (
                    <span className="text-xs text-text_secondary">
                      <strong>{timeRemaining.value}</strong>{" "}
                      {timeRemaining.suffix}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center">
                <StatusBadge status={item.status} plain />
              </div>
            </div>
          )
        })}
      </div>

      {totalPages > 1 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          rowsPerPage={ITEMS_PER_PAGE}
          onPageChange={setPage}
          onRowsPerPageChange={() => {}}
        />
      )}
    </div>
  )
}
